import { z } from 'zod';

const uuid = z.string().uuid();
const title = z.string().trim().min(2).max(200);
const logro = z.string().trim().min(3).max(400);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');

/**
 * Replaces the subjects a grade studies (scoping 3.6).
 */
export const putGradeSubjectsInputSchema = z.object({
  subjects: z
    .array(z.object({ subjectId: uuid, weeklyHours: z.number().int().min(0).max(40).optional() }))
    .max(40),
});

/**
 * A topic of the plan de estudios.
 */
export const createTopicInputSchema = z.object({
  gradeId: uuid,
  subjectId: uuid,
  periodPosition: z.number().int().min(1).max(6),
  title,
  logros: z.array(logro).max(10).default([]),
  dbaReference: z.string().trim().max(120).optional(),
});
export const updateTopicInputSchema = z
  .object({
    periodPosition: z.number().int().min(1).max(6),
    title,
    logros: z.array(logro).max(10),
    dbaReference: z.string().trim().max(120).nullable(),
  })
  .partial();

/**
 * Reorders the topics of one period: every topic of that period, in the new order.
 */
export const reorderTopicsInputSchema = z.object({
  gradeId: uuid,
  subjectId: uuid,
  periodPosition: z.number().int().min(1).max(6),
  topicIds: z.array(uuid).min(1).max(100),
});

/**
 * Seeds a grade and subject from ClassGO's DBA set; `topicIds` narrows to a selection made on
 * the preview (keys `grade:code:period:index`), otherwise the whole set for the pair.
 */
export const seedPlanInputSchema = z.object({
  gradeId: uuid,
  subjectId: uuid,
  keys: z.array(z.string().max(40)).max(100).optional(),
});

/**
 * A class-log entry: what was covered in one session (scoping 3.6).
 */
export const createClassLogInputSchema = z.object({
  groupId: uuid,
  subjectId: uuid,
  date: isoDate,
  topicId: uuid.optional(),
  timetableBlockId: uuid.optional(),
  notes: z.string().trim().max(1000).optional(),
});

/**
 * Query of the class log.
 */
export const classLogQuerySchema = z.object({
  groupId: uuid,
  subjectId: uuid.optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
});

/**
 * Body of `PUT /curriculum/grades/{gradeId}/subjects`.
 */
export type PutGradeSubjectsInput = z.input<typeof putGradeSubjectsInputSchema>;
/**
 * Body of `POST /curriculum/topics`.
 */
export type CreateTopicInput = z.input<typeof createTopicInputSchema>;
/**
 * Body of `PATCH /curriculum/topics/{id}`.
 */
export type UpdateTopicInput = z.input<typeof updateTopicInputSchema>;
/**
 * Body of `POST /curriculum/plan/reorder`.
 */
export type ReorderTopicsInput = z.input<typeof reorderTopicsInputSchema>;
/**
 * Body of `POST /curriculum/plan/seed`.
 */
export type SeedPlanInput = z.input<typeof seedPlanInputSchema>;
/**
 * Body of `POST /class-log`.
 */
export type CreateClassLogInput = z.input<typeof createClassLogInputSchema>;

/**
 * A subject a grade studies.
 */
export type GradeSubjectView = {
  subjectId: string;
  subjectName: string;
  subjectCode: string | null;
  weeklyHours: number | null;
  position: number;
  topicsCount: number;
};

/**
 * One topic of the plan.
 */
export type TopicView = {
  id: string;
  gradeId: string;
  subjectId: string;
  periodPosition: number;
  position: number;
  title: string;
  logros: string[];
  dbaReference: string | null;
  source: 'dba' | 'school';
};

/**
 * One period of the active school year as the plan lays its columns out.
 */
export type PlanPeriod = { position: number; name: string };

/**
 * The plan of one grade and subject, grouped by period position, with the active year's periods
 * (four unnamed ones when the year has none yet) so the screen shows the school's real calendar.
 */
export type PlanView = {
  gradeId: string;
  subjectId: string;
  yearPeriods: PlanPeriod[];
  periods: { periodPosition: number; topics: TopicView[] }[];
};

/**
 * One topic of ClassGO's seed as the preview shows it, with the key the seed call accepts.
 */
export type SeedPreviewTopic = {
  key: string;
  periodPosition: number;
  title: string;
  logros: string[];
  dbaReference: string;
};

/**
 * A class-log entry as listed.
 */
export type ClassLogEntryView = {
  id: string;
  groupId: string;
  subjectId: string;
  teacherUserId: string;
  date: string;
  topicId: string | null;
  topicTitle: string | null;
  notes: string | null;
};
